import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Users, Tag, Calendar, FileText } from 'lucide-react';
import { articleService } from '../services/api';

function Articles() {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await articleService.getAll();
        setArticles(response.data);
      } catch (err) {
        setError('خطا در بارگذاری مقالات');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  useEffect(() => {
    let filtered = articles.filter(article =>
      (article.status || '').trim().toLowerCase() === 'published'
    );

    if (searchTerm) {
      filtered = filtered.filter(article =>
        article.title.includes(searchTerm) ||
        article.abstract.includes(searchTerm) ||
        article.keywords.includes(searchTerm)
      );
    }

    setFilteredArticles(filtered);
  }, [searchTerm, articles]);

  const getStatusBadge = (status) => {
    switch(status) {
      case 'Published':
        return { bg: 'bg-green-100', text: 'text-green-800', label: '✓ منتشر شده' };
      case 'Under Review':
        return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '⧗ در حال بررسی' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800', label: '✎ پیش‌نویس' };
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">مقالات نشریه</h1>
          <p className="text-xl text-blue-100">جستجو و مرور مقالات علمی</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={20} className="text-blue-700" />
            <h3 className="text-lg font-bold text-gray-900">جستجو</h3>
          </div>
          <div className="relative">
            <Search className="absolute right-4 top-3.5 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="جستجو در عنوان، چکیده و کلمات کلیدی..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-12 pl-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            />
          </div>
        </div>

        {/* Results Count */}
        {!loading && (
          <div className="mb-6 text-gray-600 font-medium">
            {filteredArticles.length} مقاله یافت شد
          </div>
        )}

        {loading && <div className="text-center py-12 text-gray-500">در حال بارگذاری...</div>}
        {error && <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-8">{error}</div>}

        {!loading && filteredArticles.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <FileText size={48} className="mx-auto mb-4 opacity-30" />
            <p className="text-lg">هیچ مقاله‌ای برای نمایش وجود ندارد</p>
          </div>
        )}

        {/* Articles List */}
        <div className="space-y-4">
          {filteredArticles.map((article) => {
            const statusBadge = getStatusBadge(article.status);
            return (
              <Link
                key={article.id}
                to={`/articles/${article.id}`}
                className="block bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition overflow-hidden group"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3 gap-4">
                    <h2 className="text-lg font-bold text-gray-900 group-hover:text-blue-700 transition flex-1">
                      {article.title}
                    </h2>
                    <span className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap ${statusBadge.bg} ${statusBadge.text}`}>
                      {statusBadge.label}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                    {article.abstract}
                  </p>
                  <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 pt-4 border-t border-gray-100">
                    <span className="flex items-center gap-2">
                      <Users size={16} className="text-blue-600" />
                      {article.articleAuthors?.map(aa => aa.author.firstName + ' ' + aa.author.lastName).join('، ')}
                    </span>
                    <span className="flex items-center gap-2">
                      <Tag size={16} className="text-blue-600" />
                      {article.keywords}
                    </span>
                    <span className="flex items-center gap-2 ml-auto">
                      <Calendar size={16} className="text-blue-600" />
                      {new Date(article.submissionDate).toLocaleDateString('fa-IR')}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Articles;
